/**
 * 월드 캐릭터 표시 크기(FR-WORLD) — 운영자가 관리자에서 조절한다.
 *
 * 왜 콘텐츠인가: "그레그를 조금 더 크게" 같은 조정은 그림을 다시 뽑거나 배포하지 않고
 * 눈으로 보며 맞추는 일이다. 코드 상수로 두면 매번 개발자가 필요하다(CLAUDE.md 콘텐츠 주도).
 *
 * 값은 화면에 그려질 **세로 픽셀 수**(발끝~머리). 원본 그림 해상도와 무관하게 이 크기로 정규화된다.
 */
export interface WorldSize {
  /** 내 히어로와 상대 히어로 공통 크기. */
  hero: number;
  /** NPC id 별 크기. 없는 id 는 npcDefault 를 쓴다. */
  npc: Record<string, number>;
  /** 표에 없는 NPC 의 기본 크기. */
  npcDefault: number;
}

/** 조절 범위 — 너무 작으면 아이가 못 찾고, 너무 크면 화면을 덮는다. */
export const MIN_CHAR_PX = 32;
export const MAX_CHAR_PX = 160;
/** 손상된 입력이 표를 무한정 늘리지 못하게. */
const MAX_NPC_ENTRIES = 40;
const NPC_ID_RE = /^[a-z0-9_]{1,24}$/;

/**
 * 기본값. 그레그(`greg_white`)는 IP 주인공이라 히어로보다 **약간** 크게 잡는다 —
 * 아이가 마을에서 "누가 주인공인지" 한눈에 알아보게. 너무 키우면 아이 자신의 히어로가 작아 보인다.
 */
export const DEFAULT_WORLD_SIZE: WorldSize = {
  hero: 80,
  npcDefault: 56,
  npc: { greg_white: 92 },
};

const clampPx = (v: unknown, fallback: number): number => {
  const n = typeof v === "number" ? v : Number(v);
  if (!Number.isFinite(n)) return fallback;
  return Math.round(Math.min(MAX_CHAR_PX, Math.max(MIN_CHAR_PX, n)));
};

/** 외부 입력(D1 문서·관리자 폼) → 안전한 WorldSize. 절대 throw 하지 않는다(불변식 3·7). */
export function sanitizeWorldSize(input: unknown): WorldSize {
  const d = DEFAULT_WORLD_SIZE;
  if (!input || typeof input !== "object") return { ...d, npc: { ...d.npc } };
  const o = input as Record<string, unknown>;
  const npc: Record<string, number> = {};
  if (o.npc && typeof o.npc === "object") {
    for (const [id, v] of Object.entries(o.npc as Record<string, unknown>)) {
      if (!NPC_ID_RE.test(id)) continue; // 모르는 형식의 id 는 버린다(주입 방지)
      if (Object.keys(npc).length >= MAX_NPC_ENTRIES) break;
      npc[id] = clampPx(v, d.npcDefault);
    }
  }
  return {
    hero: clampPx(o.hero, d.hero),
    npcDefault: clampPx(o.npcDefault, d.npcDefault),
    npc: Object.keys(npc).length ? npc : { ...d.npc },
  };
}

/** 이 NPC 의 표시 크기(px). 표에 없으면 기본값. */
export function npcSizePx(size: WorldSize, npcId: string | undefined): number {
  if (npcId && Object.prototype.hasOwnProperty.call(size.npc, npcId)) return size.npc[npcId]!;
  return size.npcDefault;
}
